import { useContext, useMemo } from "react";

import { TableShell } from "@/components/tableKit";
import { Card, CardContent } from "@/components/ui/card";
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases";
import { UserContext, useRelevantDocs } from "../../../services/firebase";
import { asPlatforms } from "../../../shared/platforms";
import type { Booking, Campaign, Pitch, Talent, User } from "../../../shared/types";
import { pitchMatchesStatusFilter, pitchStatusFilterCount, type PitchStatusFilter } from "../../../utils/pitching/filter";
import { visiblePitches } from "../campaign";
import { pitchColumns } from "./pitchColumns";

type PitchesTableProps = {
    campaign: Campaign;
    booker?: User;
    pitches: Pitch[];
    pitchesLoading: boolean;
    bookings: Booking[];
    statusFilter: PitchStatusFilter;
    onApproved: () => void;
};

/** Pitches tab: its rows, columns, and the approve-to-booking menu. */
export function PitchesTable({
    campaign, booker, pitches, pitchesLoading, bookings, statusFilter, onApproved,
}: PitchesTableProps) {
    const phase = useSkeletonPhases(pitchesLoading);
    const campaignId = campaign.id;
    const user = useContext(UserContext);
    const talentIds = useMemo(() => pitches.map((pitch) => pitch.talentId), [pitches]);
    const { data: talentData } = useRelevantDocs<Talent>({
        collectionName: "talent",
        docIds: talentIds,
        enabled: talentIds.length > 0,
    });
    const talentById = useMemo(() => new Map((talentData ?? []).map((item) => [item.id, item])), [talentData]);
    const rows = useMemo(() => {
        const joined = pitches.map((pitch) => ({ ...pitch, talent: talentById.get(pitch.talentId) }));
        return visiblePitches(user, campaign, joined)
            .filter((pitch) => pitchMatchesStatusFilter(pitch, statusFilter))
            .sort((a, b) => (a.talentName || "").localeCompare(b.talentName || ""));
    }, [campaign, pitches, statusFilter, talentById, user]);
    const isFiltered = pitchStatusFilterCount(statusFilter) > 0;
    const showNotesAndRates = campaign.showNotesAndRates !== false;
    const columns = useMemo(
        () => pitchColumns({
            platforms: asPlatforms(campaign.campaignPlatform),
            showNotes: showNotesAndRates,
            showRates: showNotesAndRates,
            currency: campaign.currency || "USD",
            user,
            campaign,
            campaignId,
            bookings,
            booker,
            onApproved,
        }),
        [booker, bookings, campaign, campaignId, onApproved, showNotesAndRates, user],
    );

    if (phase === "showNothing") {
        return null;
    }

    return (
        <Card>
            <CardContent>
                <TableShell
                    columns={columns}
                    rows={rows}
                    getRowId={(row) => row.id}
                    isLoading={phase === "showSkeleton"}
                    empty={phase === "showResult" ? (
                        isFiltered ? (
                            <div className="px-4 py-16 text-center italic">No pitches match your filters.</div>
                        ) : (
                            <div className="px-4 py-16 text-center">
                                You don&apos;t have any pitches yet.
                            </div>
                        )
                    ) : null}
                />
            </CardContent>
        </Card>
    );
}
